import * as THREE from 'three';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { http } from '../../helpers';
import { Ori } from '../../assets/theme/js/variables';

const FALLBACK_MODEL_COLOR = 0xffffff;

function getViewportSize(mount) {
    return {
        width: Math.max(mount?.clientWidth || 1, 1),
        height: Math.max(mount?.clientHeight || 1, 1),
    };
}

function addStudioLights(scene) {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.65);
    scene.add(ambientLight);

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xd6e2ff, 1.05);
    scene.add(hemiLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.95);
    keyLight.position.set(1.8, 1.3, 2.4);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-1.6, 0.8, 1.2);
    scene.add(fillLight);
}

function getPointSprite(pointSpriteRef) {
    const spriteRef = pointSpriteRef;

    if (spriteRef.current) {
        return spriteRef.current;
    }

    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    context.fillRect(0, 0, size, size);

    spriteRef.current = new THREE.CanvasTexture(canvas);
    spriteRef.current.colorSpace = THREE.SRGBColorSpace;

    return spriteRef.current;
}

function hasUsableVertexColors(colorAttribute) {
    if (!colorAttribute || colorAttribute.count === 0) {
        return false;
    }

    const sampleCount = Math.min(colorAttribute.count, 500);
    const step = Math.max(1, Math.floor(colorAttribute.count / sampleCount));
    let totalLuminance = 0;
    let brightestSample = 0;
    let sampled = 0;

    for (let index = 0; index < colorAttribute.count; index += step) {
        const r = colorAttribute.getX(index);
        const g = colorAttribute.getY(index);
        const b = colorAttribute.getZ(index);
        const luminance = (0.2126 * r) + (0.7152 * g) + (0.0722 * b);

        totalLuminance += luminance;
        brightestSample = Math.max(brightestSample, luminance);
        sampled += 1;
    }

    const averageLuminance = sampled > 0 ? totalLuminance / sampled : 0;

    return brightestSample > 0.05 && averageLuminance > 0.015;
}

function buildMesh(geometry, useVertexColors, texture) {
    const materialConfig = {
        side: THREE.DoubleSide,
        roughness: useVertexColors ? 0.58 : 0.45,
        metalness: 0,
        clearcoat: 0.2,
        clearcoatRoughness: 0.08,
        envMapIntensity: texture ? 0.65 : 0.35,
        ior: 1.62,
    };

    if (texture) {
        materialConfig.map = texture;
        // Texture supplies the surface color.
        materialConfig.color = new THREE.Color(0xffffff);
    } else if (useVertexColors) {
        materialConfig.vertexColors = true;
    } else {
        materialConfig.color = new THREE.Color(FALLBACK_MODEL_COLOR);
    }

    const material = new THREE.MeshPhysicalMaterial(materialConfig);
    const mesh = new THREE.Mesh(geometry, material);

    mesh.castShadow = false;
    mesh.receiveShadow = false;

    return mesh;
}

function buildPointCloud(geometry, useVertexColors, pointSpriteRef) {
    const radius = Math.max(geometry.boundingSphere?.radius || 1, 1);
    const materialConfig = {
        size: Math.max(radius * 0.006, 0.035),
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.95,
        alphaTest: 0.35,
        depthWrite: false,
        map: getPointSprite(pointSpriteRef),
    };

    if (useVertexColors) {
        materialConfig.vertexColors = true;
    } else {
        materialConfig.color = new THREE.Color(FALLBACK_MODEL_COLOR);
    }

    const material = new THREE.PointsMaterial(materialConfig);

    return new THREE.Points(geometry, material);
}

function buildRenderable(geometry, pointSpriteRef, texture, preservePlacement = false) {
    const positionAttribute = geometry.getAttribute('position');

    if (!positionAttribute || positionAttribute.count === 0) {
        return null;
    }

    geometry.rotateX(-Math.PI / 2);
    geometry.computeBoundingSphere();

    if (!preservePlacement) {
        const center = geometry.boundingSphere?.center || new THREE.Vector3();
        geometry.translate(-center.x, -center.y, -center.z);
    }

    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    const usableVertexColors = hasUsableVertexColors(geometry.getAttribute('color'));
    const isPointCloud = geometry.index === null && !geometry.getAttribute('normal');

    // Face-less PLY files are usually scan point clouds, so render them as points
    // instead of forcing every 3 vertices into a triangle mesh.
    if (isPointCloud) {
        return buildPointCloud(geometry, usableVertexColors, pointSpriteRef);
    }

    if (!geometry.getAttribute('normal')) {
        geometry.computeVertexNormals();
    }

    return buildMesh(geometry, usableVertexColors, texture);
}

function fitCameraToRenderable(camera, controls, renderable) {
    if (!camera || !controls || !renderable) return;

    const targetCamera = camera;
    const targetControls = controls;
    const sphere = new THREE.Box3().setFromObject(renderable).getBoundingSphere(new THREE.Sphere());
    const center = sphere.center.clone();
    const radius = Math.max(sphere.radius, 0.01);
    const fitHeightDistance = radius / Math.tan(THREE.MathUtils.degToRad(targetCamera.fov * 0.5));
    const fitWidthDistance = fitHeightDistance / targetCamera.aspect;
    const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.18;
    const verticalOffset = Math.min(radius * 0.18, distance * 0.12);

    targetCamera.position.set(center.x, center.y + verticalOffset, center.z + distance);
    targetCamera.near = Math.max(distance / 100, 0.01);
    targetCamera.far = Math.max(distance * 25, 100);
    targetCamera.lookAt(center);
    targetCamera.updateProjectionMatrix();

    targetControls.target.copy(center);
    targetControls.minDistance = Math.max(distance * 0.15, 0.05);
    targetControls.maxDistance = distance * 8;
    targetControls.enablePan = true;
    targetControls.update();
}

function disposeMaterial(material, pointSprite) {
    if (Array.isArray(material)) {
        material.forEach((entry) => disposeMaterial(entry, pointSprite));
        return;
    }

    if (material.map && material.map !== pointSprite) {
        material.map.dispose();
    }

    material.dispose();
}

function disposeRenderable(renderable, pointSpriteRef) {
    if (!renderable) {
        return;
    }

    renderable.traverse((child) => {
        if (child.geometry) {
            child.geometry.dispose();
        }

        if (child.material) {
            disposeMaterial(child.material, pointSpriteRef.current);
        }
    });
}

function clearModel(stage, currentRenderableRef, pointSpriteRef) {
    const renderableRef = currentRenderableRef;

    if (!renderableRef.current) {
        return;
    }

    if (stage) {
        stage.remove(renderableRef.current);
    }

    disposeRenderable(renderableRef.current, pointSpriteRef);

    renderableRef.current = null;
}

function disposeTexture(texture, objectUrl) {
    if (texture) {
        texture.dispose();
    }

    if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
    }
}

function disposeTextureResources(textureResources) {
    Object.values(textureResources).forEach(({ texture, objectUrl }) => {
        disposeTexture(texture, objectUrl);
    });
}

function getLegacyTextureUrl(source) {
    const truncatedUrl = source.match(/^.*\./);

    return truncatedUrl ? `${truncatedUrl}jpg` : null;
}

function getNormalizedModels(scans, models, url, source, textureUrl, arrayBuffer, preservePlacement) {
    if (Array.isArray(scans) && scans.length > 0) {
        return scans
            .filter((scan) => scan && scan.plyUrl)
            .map((scan, index) => ({
                arrayBuffer: scan.arrayBuffer || null,
                key: scan.id ?? `${scan.dentalCaseID || 'case'}:${scan.displayIndex ?? index}`,
                preservePlacement: scan.preservePlacement ?? true,
                textureUrl: scan.textureUrl || null,
                url: scan.plyUrl,
            }));
    }

    if (Array.isArray(models) && models.length > 0) {
        return models
            .filter((model) => model && (model.url || model.arrayBuffer))
            .map((model, index) => ({
                arrayBuffer: model.arrayBuffer || null,
                key: model.id ?? `${index}:${model.url || model.source || 'buffer'}`,
                preservePlacement: model.preservePlacement ?? true,
                textureUrl: model.textureUrl || getLegacyTextureUrl(model.source || ''),
                url: model.url || null,
            }));
    }

    if (!url && !arrayBuffer) {
        return [];
    }

    return [{
        arrayBuffer: arrayBuffer || null,
        key: 'legacy-single',
        preservePlacement,
        textureUrl: textureUrl || (source ? getLegacyTextureUrl(source) : null),
        url: url || null,
    }];
}

function PlyViewer({
    scans,
    url,
    source,
    textureUrl,
    arrayBuffer,
    models,
    preservePlacement,
    onLoadingChange,
}) {
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const stageRef = useRef(null);
    const pmremGeneratorRef = useRef(null);
    const roomEnvironmentRef = useRef(null);
    const sceneEnvironmentRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const frameIdRef = useRef(null);
    const loadVersionRef = useRef(0);
    const pointSpriteRef = useRef(null);
    const textureResourcesRef = useRef({});
    const currentRenderableRef = useRef(null);
    const [textureStatuses, setTextureStatuses] = useState({});

    useEffect(() => () => {
        if (onLoadingChange) {
            onLoadingChange(false);
        }
    }, [onLoadingChange]);

    useEffect(() => {
        const mount = mountRef.current;

        if (!mount) {
            return undefined;
        }

        const { width, height } = getViewportSize(mount);

        sceneRef.current = new THREE.Scene();
        sceneRef.current.background = new THREE.Color(Ori.darkerGray);

        rendererRef.current = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
        });
        rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        rendererRef.current.setSize(width, height);
        rendererRef.current.outputColorSpace = THREE.SRGBColorSpace;
        rendererRef.current.toneMapping = THREE.ACESFilmicToneMapping;
        rendererRef.current.toneMappingExposure = 1.2;
        // rendererRef.current.setClearColor(0xffffff, 0);

        mount.appendChild(rendererRef.current.domElement);

        cameraRef.current = new THREE.PerspectiveCamera(1, width / height, 0.01, 5000);
        cameraRef.current.position.set(0, 0, 1);

        const headLight = new THREE.DirectionalLight(0xffffff, 0.45);
        headLight.position.set(0, 0, 1);

        // Aim straight ahead
        headLight.target.position.set(0, 0, -1);

        cameraRef.current.add(headLight);
        cameraRef.current.add(headLight.target);

        // Add camera to the scene
        sceneRef.current.add(cameraRef.current);

        controlsRef.current = new OrbitControls(cameraRef.current, rendererRef.current.domElement);
        controlsRef.current.enableDamping = true;
        controlsRef.current.dampingFactor = 0.08;
        controlsRef.current.rotateSpeed = 0.9;
        controlsRef.current.zoomSpeed = 1;
        controlsRef.current.panSpeed = 0.8;
        controlsRef.current.screenSpacePanning = true;
        controlsRef.current.target.set(0, 0, 0);

        pmremGeneratorRef.current = new THREE.PMREMGenerator(rendererRef.current);
        roomEnvironmentRef.current = new RoomEnvironment(rendererRef.current);
        sceneEnvironmentRef.current = pmremGeneratorRef.current.fromScene(roomEnvironmentRef.current, 0.04);
        // sceneRef.current.environment = sceneEnvironmentRef.current.texture;

        stageRef.current = new THREE.Group();
        sceneRef.current.add(stageRef.current);

        addStudioLights(sceneRef.current);

        const handleResize = () => {
            if (!rendererRef.current || !cameraRef.current || !mountRef.current) return;

            const viewport = getViewportSize(mountRef.current);

            rendererRef.current.setSize(viewport.width, viewport.height);
            cameraRef.current.aspect = viewport.width / viewport.height;
            cameraRef.current.updateProjectionMatrix();
        };

        if (window.ResizeObserver) {
            resizeObserverRef.current = new window.ResizeObserver(handleResize);
            resizeObserverRef.current.observe(mount);
        } else {
            window.addEventListener('resize', handleResize);
        }

        const animate = () => {
            if (controlsRef.current) {
                controlsRef.current.update();
            }

            if (rendererRef.current && sceneRef.current && cameraRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
            }

            frameIdRef.current = window.requestAnimationFrame(animate);
        };

        frameIdRef.current = window.requestAnimationFrame(animate);

        return () => {
            loadVersionRef.current += 1;

            if (frameIdRef.current) {
                window.cancelAnimationFrame(frameIdRef.current);
                frameIdRef.current = null;
            }

            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            } else {
                window.removeEventListener('resize', handleResize);
            }

            if (controlsRef.current) {
                controlsRef.current.dispose();
                controlsRef.current = null;
            }

            clearModel(stageRef.current, currentRenderableRef, pointSpriteRef);

            if (sceneEnvironmentRef.current) {
                sceneEnvironmentRef.current.dispose();
                sceneEnvironmentRef.current = null;
            }

            if (roomEnvironmentRef.current) {
                roomEnvironmentRef.current.dispose();
                roomEnvironmentRef.current = null;
            }

            if (pmremGeneratorRef.current) {
                pmremGeneratorRef.current.dispose();
                pmremGeneratorRef.current = null;
            }

            if (rendererRef.current) {
                rendererRef.current.dispose();

                if (mountRef.current && rendererRef.current.domElement.parentNode === mountRef.current) {
                    mountRef.current.removeChild(rendererRef.current.domElement);
                }

                rendererRef.current = null;
            }

            sceneRef.current = null;
            cameraRef.current = null;
            stageRef.current = null;

            if (pointSpriteRef.current) {
                pointSpriteRef.current.dispose();
                pointSpriteRef.current = null;
            }

            disposeTextureResources(textureResourcesRef.current);
            textureResourcesRef.current = {};
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const textureLoader = new THREE.TextureLoader();

        const nextModels = getNormalizedModels(
            scans,
            models,
            url,
            source,
            textureUrl,
            arrayBuffer,
            preservePlacement,
        );

        disposeTextureResources(textureResourcesRef.current);
        textureResourcesRef.current = {};

        const nextTextureStatuses = Object.fromEntries(
            nextModels.map((model) => [model.key, model.textureUrl ? 'loading' : 'idle']),
        );

        setTextureStatuses(nextTextureStatuses);

        nextModels.forEach((model) => {
            if (!model.textureUrl) {
                return;
            }

            http.getBlob(model.textureUrl, 'image/jpeg')
                .then((response) => {
                    if (cancelled) {
                        return;
                    }

                    const imageBlob = new Blob(
                        [response.data],
                        {
                            type: response.headers['content-type'],
                        },
                    );

                    const objectUrl = URL.createObjectURL(imageBlob);
                    const texture = textureLoader.load(
                        objectUrl,
                        () => {
                            if (cancelled) {
                                return;
                            }

                            if (textureResourcesRef.current[model.key]?.objectUrl === objectUrl) {
                                URL.revokeObjectURL(objectUrl);
                                textureResourcesRef.current[model.key] = {
                                    objectUrl: null,
                                    texture,
                                };
                            }

                            setTextureStatuses((currentStatuses) => ({
                                ...currentStatuses,
                                [model.key]: 'ready',
                            }));
                        },
                        undefined,
                        (error) => {
                            if (cancelled) {
                                return;
                            }

                            console.error('Failed to load texture image.', error);

                            if (textureResourcesRef.current[model.key]?.texture === texture) {
                                delete textureResourcesRef.current[model.key];
                            }

                            texture.dispose();
                            URL.revokeObjectURL(objectUrl);

                            setTextureStatuses((currentStatuses) => ({
                                ...currentStatuses,
                                [model.key]: 'failed',
                            }));
                        },
                    );

                    texture.colorSpace = THREE.SRGBColorSpace;
                    texture.flipY = true;
                    texture.needsUpdate = true;

                    textureResourcesRef.current[model.key] = {
                        objectUrl,
                        texture,
                    };

                    console.log('Done for model', model.key);
                })
                .catch(() => {
                    if (cancelled) {
                        return;
                    }

                    console.warn('Failed to load scan texture');
                    setTextureStatuses((currentStatuses) => ({
                        ...currentStatuses,
                        [model.key]: 'failed',
                    }));
                });
        });

        return () => {
            cancelled = true;
        };
    }, [arrayBuffer, models, preservePlacement, scans, source, textureUrl, url]);

    useEffect(() => {
        if (!stageRef.current) {
            return undefined;
        }

        clearModel(stageRef.current, currentRenderableRef, pointSpriteRef);

        const nextModels = getNormalizedModels(
            scans,
            models,
            url,
            source,
            textureUrl,
            arrayBuffer,
            preservePlacement,
        );

        if (nextModels.length === 0) {
            if (onLoadingChange) {
                onLoadingChange(false);
            }
            return undefined;
        }

        if (onLoadingChange) {
            onLoadingChange(true);
        }

        const hasLoadingTexture = nextModels.some(
            (model) => model.textureUrl && textureStatuses[model.key] !== 'ready' && textureStatuses[model.key] !== 'failed',
        );

        console.log('texture statuses', textureStatuses);
        console.log('normalized models', nextModels);
        if (hasLoadingTexture) {
            return undefined;
        }

        const loader = new PLYLoader();
        const loadVersion = loadVersionRef.current + 1;
        loadVersionRef.current = loadVersion;
        let cancelled = false;

        const loadGeometry = (model) => {
            if (model.arrayBuffer) {
                return Promise.resolve(loader.parse(model.arrayBuffer));
            }

            return http.getBlob(model.url, 'application/sla')
                .then((response) => loader.parse(response.data));
        };

        Promise.all(nextModels.map(async (model) => {
            try {
                const geometry = await loadGeometry(model);

                if (cancelled || loadVersion !== loadVersionRef.current) {
                    geometry.dispose();
                    return null;
                }

                const texture = textureResourcesRef.current[model.key]?.texture || null;
                const renderable = buildRenderable(geometry, pointSpriteRef, texture, model.preservePlacement);
                console.log('Built renderable for', model.key, renderable);

                if (!renderable) {
                    geometry.dispose();
                    return null;
                }

                return renderable;
            } catch (error) {
                console.error('Failed to load PLY model.', error);
                return null;
            }
        }))
            .then((renderables) => {
                if (cancelled || loadVersion !== loadVersionRef.current) {
                    renderables.forEach((renderable) => disposeRenderable(renderable, pointSpriteRef));
                    return;
                }

                const nextRenderableGroup = new THREE.Group();
                const validRenderables = renderables.filter(Boolean);

                if (validRenderables.length === 0) {
                    if (onLoadingChange) {
                        onLoadingChange(false);
                    }
                    return;
                }

                validRenderables.forEach((renderable) => {
                    nextRenderableGroup.add(renderable);
                });

                currentRenderableRef.current = nextRenderableGroup;
                stageRef.current.add(nextRenderableGroup);
                console.log('Added group to scene', validRenderables.length, nextRenderableGroup);
                fitCameraToRenderable(cameraRef.current, controlsRef.current, nextRenderableGroup);

                if (onLoadingChange) {
                    onLoadingChange(false);
                }
            });

        return () => {
            cancelled = true;
            loadVersionRef.current += 1;
        };
    }, [arrayBuffer, models, onLoadingChange, preservePlacement, scans, source, textureStatuses, textureUrl, url]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
            }}
            ref={mountRef}
        />
    );
}

PlyViewer.propTypes = {
    scans: PropTypes.arrayOf(
        PropTypes.shape({
            arrayBuffer: PropTypes.instanceOf(ArrayBuffer),
            dentalCaseID: PropTypes.string,
            displayIndex: PropTypes.number,
            id: PropTypes.string,
            plyUrl: PropTypes.string,
            preservePlacement: PropTypes.bool,
            textureUrl: PropTypes.string,
        }),
    ),
    models: PropTypes.arrayOf(
        PropTypes.shape({
            arrayBuffer: PropTypes.instanceOf(ArrayBuffer),
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            preservePlacement: PropTypes.bool,
            source: PropTypes.string,
            textureUrl: PropTypes.string,
            url: PropTypes.string,
        }),
    ),
    preservePlacement: PropTypes.bool,
    textureUrl: PropTypes.string,
    url: PropTypes.string,
    source: PropTypes.string,
    arrayBuffer: PropTypes.instanceOf(ArrayBuffer),
    onLoadingChange: PropTypes.func,
};

PlyViewer.defaultProps = {
    scans: null,
    models: null,
    preservePlacement: false,
    textureUrl: null,
    url: null,
    source: null,
    arrayBuffer: null,
    onLoadingChange: null,
};

export default PlyViewer;
